/* ═══════════════════════════════════════════════════════════════
   TasksToolbar — the ONE toolbar driving both Board and List (Decision 2):
   title + count, view switch, search, Filter, Group/Columns/Aa (List-only,
   shown disabled on Board per Decision 2), Show archived, New task. Follows
   the app's table-standard toolbar shape (Hub.tsx / DirectoryTab.tsx) —
   Button/Input/Icon from the kit, tokens only, no raw <button>/<input>.
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import { Eye, EyeOff, Filter as FilterIcon, Layers, Plus, Rows3, X } from 'lucide-react';
import type { Project, TeamMember } from '../../../shared/types';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Icon } from '../../components/ui/Icon';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FilterBuilder } from '../../components/FilterBuilder';
import { activeRuleCount, type TableFilterState } from '../../components/filterModel';
import { buildTaskFilterFields } from './taskFilterFields';
import type { ViewMode } from './types';

export interface TasksToolbarProps {
  taskCount: number;
  viewMode: ViewMode;
  onViewModeChange: (v: ViewMode) => void;
  canCreateTask: boolean;
  onNewTask: () => void;
  projects: Project[];
  members: TeamMember[];
  filterSearch: string;
  onFilterSearchChange: (v: string) => void;
  /** "Two doors" project scope (Decision 1) — a removable chip, never a
   *  Filter-menu rule, so it is never persisted. */
  scopeProjectId: string;
  scopeProjectName: string;
  onClearScopeProject: () => void;
  filterState: TableFilterState;
  onFilterStateChange: (s: TableFilterState) => void;
  totalRows: number;
  countFilterMatches: (s: TableFilterState) => number;
  showArchived: boolean;
  onToggleShowArchived: () => void;
}

export function TasksToolbar({
  taskCount,
  viewMode,
  onViewModeChange,
  canCreateTask,
  onNewTask,
  projects,
  members,
  filterSearch,
  onFilterSearchChange,
  scopeProjectId,
  scopeProjectName,
  onClearScopeProject,
  filterState,
  onFilterStateChange,
  totalRows,
  countFilterMatches,
  showArchived,
  onToggleShowArchived,
}: TasksToolbarProps) {
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const activeCount = activeRuleCount(filterState);
  const isList = viewMode === 'list';
  // Group/Columns/Aa act on the List only — DataTable renders the real,
  // functional controls in its own toolbar row directly under this one.
  // On Board there is nothing for them to act on, so they stay visible
  // (identical control set in both views) but disabled.
  const listOnlyTitle = 'List view only';

  return (
    <div className="flex flex-shrink-0 flex-col gap-2">
      {/* Header row — title/count, view switch, New task */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="m-0 text-2xl font-bold text-text">Tasks</h2>
          <p className="m-0 mt-0.5 text-base text-muted">
            {taskCount} task{taskCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={(v) => onViewModeChange(v as ViewMode)}>
            <TabsList>
              <TabsTrigger value="board">Board</TabsTrigger>
              <TabsTrigger value="list">List</TabsTrigger>
            </TabsList>
          </Tabs>
          {canCreateTask && (
            <Button variant="default" size="sm" onClick={onNewTask}>
              <Icon icon={Plus} em={1.4} />
              New task
            </Button>
          )}
        </div>
      </div>

      {/* Controls row — search (shared), Filter (Board), Group/Columns/Aa
          (disabled stand-ins on Board), Show archived. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-[13.75rem]">
          <Input
            value={filterSearch}
            onChange={(e) => onFilterSearchChange(e.target.value)}
            placeholder="Search tasks…"
            aria-label="Search tasks"
            className="h-8 pr-7 text-sm"
          />
          {filterSearch && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onFilterSearchChange('')}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 h-5 w-5 -translate-y-1/2 p-0 text-muted"
            >
              <Icon icon={X} em={0.9231} />
            </Button>
          )}
        </div>

        {/* Filter — the List's own Filter button (DataTable's own toolbar,
            one row below) is canonical there; this one drives Board, and
            both edit the SAME lifted `filterState`. */}
        {!isList && (
          <Button
            variant={activeCount > 0 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterBuilderOpen(true)}
            aria-haspopup="dialog"
          >
            <Icon icon={FilterIcon} em={1.15} />
            Filter
            {activeCount > 0 && <span className="text-xs font-bold">({activeCount})</span>}
          </Button>
        )}

        {!isList && (
          <>
            <Button variant="outline" size="sm" disabled title={listOnlyTitle}>
              <Icon icon={Layers} em={1.15} />
              Group
            </Button>
            <Button variant="outline" size="sm" disabled title={listOnlyTitle}>
              <Icon icon={Rows3} em={1.15} />
              Columns
            </Button>
            <Button variant="outline" size="sm" disabled title={listOnlyTitle}>
              Aa
            </Button>
          </>
        )}

        <Button
          variant={showArchived ? 'secondary' : 'outline'}
          size="sm"
          aria-pressed={showArchived}
          disabled={!isList}
          title={isList ? undefined : 'Board never shows archived tasks'}
          onClick={onToggleShowArchived}
        >
          <Icon icon={showArchived ? EyeOff : Eye} em={1.2} />
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>

        {scopeProjectId && (
          <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-hover px-2.5 text-sm text-text">
            Project: {scopeProjectName || 'Untitled'}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClearScopeProject}
              aria-label="Clear project filter"
              className="h-4 w-4 p-0 text-muted"
            >
              <Icon icon={X} em={0.9} />
            </Button>
          </span>
        )}
      </div>

      {!isList && (
        <FilterBuilder
          open={filterBuilderOpen}
          onClose={() => setFilterBuilderOpen(false)}
          fields={buildTaskFilterFields(projects, members)}
          value={filterState}
          onApply={onFilterStateChange}
          totalRows={totalRows}
          countMatches={countFilterMatches}
        />
      )}
    </div>
  );
}
